/**
 * Static registry of custom component field requirements.
 *
 * When a custom component is used in a list configuration, the backend needs
 * to know which data fields to include in the API response. This map declares
 * those requirements so that `extractRequiredFields` can include them when
 * resolving fields for a saved list.
 *
 * Lives in libs/core so both frontend (web-ui) and backend (crm-api) can
 * import it — unlike the ComponentRegistry in web-ui which holds React
 * components and is frontend-only.
 *
 * Only components that need fields beyond their standard dataConfig entry
 * need to be listed here. Components that only render the value from their
 * configured data source (field/template/custom_text) don't need entries.
 */

interface ComponentFieldRequirement {
	fieldName: string;
	objectTable?: string;
}

const COMPONENT_FIELD_REQUIREMENTS: Record<
	string,
	Array<ComponentFieldRequirement>
> = {
	// Item attribute icons — server-resolved effective schedule + boolean item flags
	'shop-item-attribute-icons': [
		{ fieldName: 'effectiveDeaSchedule' },
		{ fieldName: 'isShortDate' },
		{ fieldName: 'isRefrigerated' },
		{ fieldName: 'isHazmat' },
	],

	// 12-month trend charts — monthly aggregate data arrays
	'item-client-history-trend-chart': [
		{ fieldName: 'monthlyData' },
		{ fieldName: 'trend' },
	],
	'client-item-history-trend-chart': [
		{ fieldName: 'monthlyData' },
		{ fieldName: 'trend' },
	],

	// PD-13163 — client list 1Y Sales sparkline. Same field shape as the
	// item-history charts above, attached from the client_sales monthly rollup.
	'client-sales-trend-chart': [
		{ fieldName: 'monthlyData' },
		{ fieldName: 'trend' },
	],

	// PD-17293 — the customer's monthly price for the item (Price History).
	'client-price-history-trend-chart': [
		{ fieldName: 'months' },
		{ fieldName: 'firstPrice' },
		{ fieldName: 'latestPrice' },
	],

	// Quantity summary displays — monthly data for max/average calculations
	'item-client-history-quantities': [
		{ fieldName: 'monthlyData' },
		{ fieldName: 'quantityLast12Months' },
	],
	'client-item-history-quantities': [
		{ fieldName: 'monthlyData' },
		{ fieldName: 'quantityLast12Months' },
	],

	// AI agent activity sparkline
	'conversation-agent-activity-sparkline': [
		{ fieldName: 'monthlySessionData' },
		{ fieldName: 'trend' },
	],

	// PD-16920 — the agent's template standing (`template` on the row) is
	// computed from these columns.
	'conversation-agent-template-badge': [
		{ fieldName: 'templateKey' },
		{ fieldName: 'templateVersion' },
		{ fieldName: 'templateCustomized' },
	],

	// Shop limit warning — order/daily/monthly limit fields. monthQuantity is
	// the rolling last-30-days ordered quantity (monthly limit, PD-14038).
	'shop-limit-warning': [
		{ fieldName: 'perOrderLimit' },
		{ fieldName: 'dailyLimit' },
		{ fieldName: 'todayQuantity' },
		{ fieldName: 'monthlyLimit' },
		{ fieldName: 'monthQuantity' },
	],

	// Price-change indicator — needs the price-history fields server-side so
	// the component can compute the warning(s) on the client. `lastOrderedListPrice`
	// is address-scoped and only populated when an addressId is selected;
	// `previousPrice` / `priceModifiedDatetime` live on the item row.
	'shop-price-change-indicator': [
		{ fieldName: 'previousPrice' },
		{ fieldName: 'priceModifiedDatetime' },
		{ fieldName: 'lastOrderedListPrice' },
	],

	// PD-11124 items-list price-change display — compares the current list
	// price against the price ItemPriceTrackingSubscriber rolled into
	// `previousPrice`. `priceModifiedDatetime` is declared so the column keeps
	// its data even on a list whose date filter/sort was removed.
	'item-price-change': [
		{ fieldName: 'price' },
		{ fieldName: 'previousPrice' },
		{ fieldName: 'priceModifiedDatetime' },
	],

	// Promo badge — server-enriched fields attached by ClientItemService.
	// `_enrichWithPromoBadges`; without these in the response the badge has
	// nothing to render.
	'shop-promo-badge': [
		{ fieldName: 'promoBadgeText' },
		{ fieldName: 'promoHeadlineText' },
		{ fieldName: 'promoId' },
	],

	// Price-contract indicator — `_contractPriceBreaks` and `_listPrice` are
	// attached client-side by ClientShopListWrapper from the response's
	// `contractPrices` block, so no extra server-side field requirements here.

	// Order line items "Save Price" action — needs to compare the line price
	// against the item's default and minimum, and to know which item to upsert
	// the saved price for. The order's client is read from OrderContext on the
	// frontend, so no client-id field requirement here.
	'order-save-price-action': [
		{ fieldName: 'price', objectTable: 'order_detail' },
		{ fieldName: 'minimumPrice', objectTable: 'order_detail' },
		{ fieldName: 'itemPrice' },
		{ fieldName: '_fk_item__id' },
	],

	// Cart line cells (PD-13666). All six read the row directly rather than a
	// configured data source, so the fields they depend on are declared here to
	// keep them selected no matter how the columns are configured.
	'cart-quantity-input': [
		{ fieldName: '_fk_item__id' },
		{ fieldName: 'price', objectTable: 'order_detail' },
		{ fieldName: 'perOrderLimit', objectTable: 'order_detail' },
		{ fieldName: 'quantityOrdered', objectTable: 'order_detail' },
	],
	'cart-price-input': [
		{ fieldName: '_fk_item__id' },
		{ fieldName: 'price', objectTable: 'order_detail' },
		{ fieldName: 'minimumPrice', objectTable: 'order_detail' },
	],
	'cart-actions-wrapper': [{ fieldName: '_fk_item__id' }],
	'cart-item-badges': [
		{ fieldName: '_id', objectTable: 'order_detail' },
		{ fieldName: '_fk_item__id' },
		{ fieldName: 'isShortDate' },
		{ fieldName: 'itemIsActive' },
	],
	'cart-surcharge-label': [{ fieldName: '_fk_item__id' }],

	// PD-17691 — the Storefront Users Clients cell draws each account from the
	// row's structured `clients`; its configured value (`clientSummary`) is
	// only the text the CSV export writes.
	'ecommerce-user-clients': [{ fieldName: 'clients' }],

	// Lifecycle stage chip — the component reads the FK directly off the row
	// (not the configured data source) so it can do an in-place update via
	// ClientQueryService. `leadOrCustomer` gates the inline-edit permission
	// check; `lifecycleStageName` is kept so sorting/filtering by the column
	// continues to work after the data-source picker is hidden.
	'client-lifecycle-stage-selector': [
		{ fieldName: 'fkClientLifecycleStageId' },
		{ fieldName: 'leadOrCustomer' },
		{ fieldName: 'lifecycleStageName' },
	],
};

/**
 * Returns the field requirements for a custom component, or null if the
 * component has no special requirements declared.
 */
export function getComponentFieldRequirements(
	componentId: string,
): Array<ComponentFieldRequirement> | null {
	return COMPONENT_FIELD_REQUIREMENTS[componentId] ?? null;
}

/**
 * Components that are fully self-contained — they extract their own data
 * from the row and don't need the user to configure a Data Source, Style,
 * Color, or Value Mapping in the field configurator.
 *
 * Components NOT in this set still use the standard data source to get
 * their display value (e.g. Status Indicator reads the configured field
 * and applies conditional formatting).
 */
const SELF_CONTAINED_COMPONENTS = new Set<string>([
	'shop-item-attribute-icons',
	'item-client-history-trend-chart',
	'client-item-history-trend-chart',
	'client-sales-trend-chart',
	'client-price-history-trend-chart',
	'item-client-history-quantities',
	'client-item-history-quantities',
	'item-client-history-purchase-info',
	'client-item-history-purchase-info',
	'item-client-history-client',
	'item-price-change',
	'client-item-history-item',
	'conversation-agent-avatar',
	'conversation-agent-activity-sparkline',
	'conversation-agent-template-badge',
	'agent-actions-badge',
	'cart-quantity-input',
	'cart-price-input',
	'cart-actions-wrapper',
	'cart-item-badges',
	'cart-surcharge-label',
	'client-shop-quantity-input',
	'client-shop-price-input',
	'client-shop-actions-wrapper',
	'quote-shop-quantity-input',
	'quote-shop-price-input',
	'invoice-shop-quantity-input',
	'invoice-shop-price-input',
	'shop-limit-warning',
	'competitive-price-action',
	'shop-price-change-indicator',
	'shop-price-contract-indicator',
	'shop-promo-badge',
	'order-save-price-action',
	'client-lifecycle-stage-selector',
]);

/**
 * Returns true if the component handles its own data extraction and
 * doesn't need the standard Data Source / Style / Color configurators.
 */
export function isComponentSelfContained(componentId: string): boolean {
	return SELF_CONTAINED_COMPONENTS.has(componentId);
}

/**
 * PD-16646 — custom components that only DRAW from their data source and carry no value
 * of their own for a spreadsheet: charts and sparklines (fed the row id or a monthly
 * series), avatars, and editing or action controls. A CSV export leaves them out;
 * exporting their data source put the client id under "1Y Sales". Every other custom
 * component (the lifecycle stage selector, status indicators…) renders its configured
 * value and is exported.
 */
const NON_EXPORTABLE_COMPONENTS = new Set<string>([
	'item-client-history-trend-chart',
	'client-item-history-trend-chart',
	'client-sales-trend-chart',
	'client-price-history-trend-chart',
	'conversation-agent-avatar',
	'conversation-agent-activity-sparkline',
	'cart-quantity-input',
	'cart-price-input',
	'cart-actions-wrapper',
	'client-shop-quantity-input',
	'client-shop-price-input',
	'client-shop-actions-wrapper',
	'quote-shop-quantity-input',
	'quote-shop-price-input',
	'invoice-shop-quantity-input',
	'invoice-shop-price-input',
	'competitive-price-action',
	'order-save-price-action',
]);

/** Whether a custom component's value belongs in a CSV export. */
export function isComponentExportable(componentId: string): boolean {
	return !NON_EXPORTABLE_COMPONENTS.has(componentId);
}
